import React, { useState } from "react";
import { Gamepad2 } from "lucide-react";
import api from "@/lib/api";

const WINNING_LINES = [
  [0, 1, 2], [3, 4, 5], [6, 7, 8],
  [0, 3, 6], [1, 4, 7], [2, 5, 8],
  [0, 4, 8], [2, 4, 6],
];

function winner(board) {
  for (const [a, b, c] of WINNING_LINES) {
    if (board[a] && board[a] === board[b] && board[a] === board[c]) return board[a];
  }
  return null;
}

export default function GameCard({ game, userId, onUpdate }) {
  const isConnect4 = game.game_type === "connect4";
  const isChess = game.game_type === "chess";
  const board = game.game_board || Array(isChess ? 64 : isConnect4 ? 42 : 9).fill(null);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const mine = game.game_players?.[0] === userId ? "X" : "O";
  const chessColor = isChess ? (mine === "X" ? "w" : "b") : null;
  const chessSymbols = { K: "♔", Q: "♕", R: "♖", B: "♗", N: "♘", P: "♙", k: "♚", q: "♛", r: "♜", b: "♝", n: "♞", p: "♟" };
  const other = mine === "X" ? "O" : "X";
  const win = isConnect4 ? null : winner(board);
  const connect4Winner = isConnect4 ? game.game_winner : null;
  const draw = isConnect4
    ? (!connect4Winner && board.every(Boolean))
    : (!win && board.every(Boolean));
  const myTurn = game.game_status === "active" && game.game_turn === userId;
  const finished = game.game_status !== "active";
  const lastMove = game.game_last_move;
  const lastColumn = isConnect4 && Number.isInteger(lastMove?.column) ? lastMove.column : null;
  const lastMoveMine = lastMove?.player_id === userId;
  const resultTitle = game.game_winner === userId ? "You Won! 🎉" : game.game_winner ? "You Lost" : "Draw 🤝";
  const turnLabel = lastMove ? `${lastMoveMine ? "You" : "Opponent"} played${isConnect4 && lastColumn !== null ? ` · Column ${lastColumn + 1}` : ""}` : (myTurn ? "Your turn" : "Their turn");

  const play = async (index) => {
    if (!myTurn || finished) return;
    if (isChess) {
      if (selectedSquare === null) {
        if (board[index] && ((chessColor === "w" && board[index] === board[index].toUpperCase()) || (chessColor === "b" && board[index] === board[index].toLowerCase()))) setSelectedSquare(index);
        return;
      }
      if (selectedSquare !== index && board[index] && ((chessColor === "w" && board[index] === board[index].toUpperCase()) || (chessColor === "b" && board[index] === board[index].toLowerCase()))) {
        setSelectedSquare(index);
        return;
      }
      try {
        const { data } = await api.post(`/games/${game.id}/move`, { from_index: selectedSquare, to_index: index, promotion: "q" });
        onUpdate(data);
      } catch {}
      setSelectedSquare(null);
      return;
    }
    if (!isConnect4 && board[index]) return;
    if (isConnect4 && board[index]) return;
    try {
      const { data } = await api.post(`/games/${game.id}/move`, { index });
      onUpdate(data);
    } catch {}
  };

  const title = isChess
    ? (finished ? (game.game_winner === userId ? "You won 🎉" : game.game_winner ? "You lost" : "Draw 🤝") : game.game_check ? "Check!" : myTurn ? "Your turn" : "Their turn")
    : (win || connect4Winner)
      ? (game.game_winner === userId ? "You won 🎉" : "You lost")
      : draw
        ? "Draw 🤝"
        : myTurn
          ? "Your turn"
          : "Their turn";

  const connectDisc = (cell, isLastMove) => {
    if (!cell) return <span className="block h-full w-full rounded-full bg-white/95 shadow-inner" />;
    return (
      <span
        className={`block h-full w-full rounded-full border-2 shadow-sm transition ${cell === "X"
          ? "border-red-500 bg-red-500"
          : "border-yellow-400 bg-yellow-400"} ${isLastMove ? "ring-4 ring-white ring-offset-2 ring-offset-violet-700 scale-105" : ""}`}
      />
    );
  };

  return (
    <div className="w-[min(320px,78vw)] overflow-hidden rounded-3xl border border-pink-100 bg-white p-3 shadow-sm">
      <div className="mb-3 flex items-center gap-2 px-1">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-pink-500 to-violet-600 text-white shadow-sm">
          <Gamepad2 className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-black uppercase tracking-wider text-pink-500">Game</p>
          <p className="text-sm font-extrabold text-slate-900">{isChess ? "Chess" : isConnect4 ? "Connect 4" : "Tic-Tac-Toe"}</p>
        </div>
        <span className="text-[10px] font-bold text-slate-400">{title}</span>
      </div>

      {isChess ? (
        <div className="relative overflow-hidden rounded-2xl border border-amber-200 bg-[#ead8b0] p-1.5 shadow-inner">
          <div className="grid grid-cols-8 overflow-hidden rounded-xl">
            {Array.from({ length: 64 }, (_, displayIndex) => {
              const displayRow = Math.floor(displayIndex / 8);
              const displayCol = displayIndex % 8;
              const actualRow = chessColor === "w" ? displayRow : 7 - displayRow;
              const actualCol = chessColor === "w" ? displayCol : 7 - displayCol;
              const index = actualRow * 8 + actualCol;
              const piece = board[index];
              const light = (actualRow + actualCol) % 2 === 0;
              const isSelected = selectedSquare === index;
              const lastFrom = game.game_last_move?.from_index;
              const lastTo = game.game_last_move?.to_index;
              const isLast = index === lastFrom || index === lastTo;
              return (
                <button
                  key={displayIndex}
                  type="button"
                  disabled={!myTurn || finished}
                  onClick={() => play(index)}
                  className={`relative aspect-square flex items-center justify-center text-[clamp(22px,8vw,34px)] leading-none transition ${light ? "bg-[#f4e4c4]" : "bg-[#a9784b]"} ${isSelected ? "ring-4 ring-inset ring-pink-400" : ""} ${isLast ? "after:absolute after:inset-1 after:rounded-md after:ring-2 after:ring-pink-300/70" : ""} disabled:cursor-default`}
                  aria-label={piece ? `Chess square ${index + 1}` : "Empty chess square"}
                >
                  <span className={piece && piece === piece.toUpperCase() ? "text-white drop-shadow-[0_2px_1px_rgba(0,0,0,.55)]" : "text-slate-900 drop-shadow-[0_1px_0_rgba(255,255,255,.35)]"}>{piece ? chessSymbols[piece] : ""}</span>
                </button>
              );
            })}
          </div>
          {finished && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-slate-950/25 p-4 backdrop-blur-[1px]">
              <div className="rounded-2xl border border-white/30 bg-white/95 px-7 py-4 text-center shadow-2xl">
                <p className={`text-xl font-black ${game.game_winner === userId ? "text-pink-600" : game.game_winner ? "text-violet-600" : "text-slate-700"}`}>{resultTitle}</p>
                <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Game over</p>
              </div>
            </div>
          )}
        </div>
      ) : isConnect4 ? (
        <div className="relative rounded-2xl bg-violet-700 p-2.5 shadow-inner">
          <div className="grid grid-cols-7 gap-1.5">
            {Array.from({ length: 7 }, (_, column) => {
              const full = !!board[column];
              const columnIsLast = lastColumn === column;
              return (
                <button
                  key={column}
                  type="button"
                  disabled={!myTurn || finished || full}
                  onClick={() => play(column)}
                  aria-label={`Drop piece in column ${column + 1}`}
                  className={`group flex min-w-0 flex-col gap-1.5 rounded-xl p-0.5 transition disabled:cursor-default ${columnIsLast ? "bg-white/10 ring-1 ring-white/25" : "hover:bg-white/10"}`}
                >
                  {Array.from({ length: 6 }, (_, row) => {
                    const cell = board[row * 7 + column];
                    return (
                      <span key={row} className="block aspect-square w-full rounded-full bg-violet-900/45 p-[2px]">
                        {connectDisc(cell, lastMove?.index === row * 7 + column)}
                      </span>
                    );
                  })}
                </button>
              );
            })}
          </div>
          <div className="mt-2 grid grid-cols-7 gap-1.5 px-1">
            {Array.from({ length: 7 }, (_, column) => (
              <span key={column} className={`text-center text-[9px] font-bold ${lastColumn === column ? "text-white" : "text-white/45"}`}>{lastColumn === column ? "↓" : column + 1}</span>
            ))}
          </div>
          {finished && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-slate-950/25 p-4 backdrop-blur-[1px]">
              <div className="rounded-2xl border border-white/30 bg-white/95 px-7 py-4 text-center shadow-2xl">
                <p className={`text-xl font-black ${game.game_winner === userId ? "text-pink-600" : game.game_winner ? "text-violet-600" : "text-slate-700"}`}>{resultTitle}</p>
                <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Game over</p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="relative">\n        <div className="grid grid-cols-3 gap-2">
          {board.map((cell, index) => (
            <button
              key={index}
              type="button"
              disabled={!myTurn || !!cell || finished}
              onClick={() => play(index)}
              className={`aspect-square rounded-2xl border text-xl font-black transition ${
                cell === "X"
                  ? "border-pink-200 bg-pink-50 text-pink-500"
                  : cell === "O"
                    ? "border-violet-200 bg-violet-50 text-violet-600"
                    : "border-slate-100 bg-slate-50 text-transparent hover:bg-white"
              } disabled:cursor-default`}
            >
              {cell || "+"}
            </button>
          ))}
        </div>\n          {finished && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-slate-950/25 p-4 backdrop-blur-[1px]">
              <div className="rounded-2xl border border-white/40 bg-white/95 px-7 py-5 text-center shadow-2xl">
                <p className={`text-2xl font-black ${game.game_winner === userId ? "text-pink-600" : game.game_winner ? "text-violet-600" : "text-slate-700"}`}>{resultTitle}</p>
                <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Game over</p>
              </div>
            </div>
          )}\n        </div>
      )}

      <div className="mt-3 flex items-center justify-between px-1 text-[10px] font-semibold text-slate-400">
        <span>{isChess ? `You: ${chessColor === "w" ? "♔ White" : "♚ Black"} · Friend: ${chessColor === "w" ? "♚ Black" : "♔ White"}` : `You: ${isConnect4 ? (mine === "X" ? "🔴" : "🟡") : mine} · Friend: ${isConnect4 ? (other === "X" ? "🔴" : "🟡") : other}`}</span>
        {finished && <span>Game over</span>}
      </div>
    </div>
  );
}
